import { SWRConfiguration } from "swr";

import { FetcherResult } from "../api-client/fetcher";
import { ModifiedSWRResponse, useSwrGet } from "../api-client/useSwrGet";
import { useTireSeason } from "../blocks/Tires/Results/Filters/TireSeasonFilters/useTireSeason";
import { useTireSize } from "../blocks/Tires/Results/Filters/TireSizeFilters/useTireSize";
import { PartTypeTire } from "../constants/partTypes";
import { productTypeCombo, productTypeTire } from "../constants/productTypes";
import { LanguageMap } from "../i18n/useLanguage";
import { DealerVehicleResultsRoute } from "../navigation/DealerRoutes";
import { useRouteParams } from "../navigation/useRouteParams";
import { useGetVehicleInfo } from "./useGetVehicleInfo";
import { SeasonNumber } from "./useGetVehicleInfoByVehicleKeys";

const productTypes = new Set<string | undefined>([
  productTypeTire,
  productTypeCombo,
]);

export const useSearchTiresByVehicle = (
  swrOptions: SWRConfiguration<FetcherResult<TireSearchResultsPayload>> = {}
): ModifiedSWRResponse<
  FetcherResult<TireSearchResultsPayload>,
  TireSearchResultsPayload
> => {
  const route = useRouteParams(DealerVehicleResultsRoute);
  const { data: vehicleInfo } = useGetVehicleInfo();

  const [season] = useTireSeason();
  const [tireSize] = useTireSize();

  const shouldFetch =
    Boolean(route) &&
    Boolean(vehicleInfo?.vehicleId) &&
    Boolean(tireSize) &&
    productTypes.has(route?.productType);

  const { data, ...result } = useSwrGet<TireSearchResultsPayload>(
    shouldFetch
      ? "/retailSiteWebService/parts/tire/searchTiresByVehicle.php"
      : null,
    {
      dealerId: route?.dealerId || "",
      isNational: "0",
      originalEquipmentFilter: "ALL",
      searchFailoverMethod: "TIRESIZE",
      season: String(season),
      vehicleId: String(vehicleInfo?.vehicleId || ""),
      vehicleSubmodelTireSizeId: tireSize,
    },
    swrOptions
  );

  return { ...result, data };
};

export type TireSearchResultsPayload = {
  featuredPartList: FeaturedPartListItem[];
  partList: TirePartItem[];
  partListSummary: PartListSummary;
  popularPartList?: TirePartItem[];
  resultParameters: ResultParameters;
};

export type FeaturedPartListItem = {
  part: TirePartItem;
  position: number;
};

export type TirePartItem = {
  partDetails: {
    primaryPart: PrimaryTirePart;
    relatedParts: TirePartSummary[];
    secondaryParts: PrimaryTirePart[];
  };
  partPricing: {
    primaryPart: TirePartPricing;
    relatedParts: TirePartPricing[];
    secondaryParts: TirePartPricing[];
  };
  partStock: {
    primaryPart: StockItem[];
    relatedParts: StockItem[];
    secondaryParts: StockItem[];
  };
  partSummary: {
    primaryPart: TirePartSummary;
    relatedParts: TirePartSummary[];
    secondaryParts: TirePartSummary[];
  };
  partTire?: never;
  partWheel?: never;
};

export type StockItem = {
  itemId: string;
  itemType: PartTypeTire;
  minutesSinceLastStockUpdate: number;
  optionId: string;
  sortOrder: string;
  stockApiForceRefreshOnLoad: boolean;
  stockApiQueryUrl: string;
  stockLocationId: string;
  stockLocationKey: string;
  stockLocationName: string;
  stockLocationType: string;
  stockLocationWebsite: string;
  stockQty: string;
  stockQtyLocal: string;
  stockQtyLocalPlus: string;
  stockQtyNationWide: string;
  tireLinkEnabled: boolean;
  tireStockApiQueryEngineCode: string;
  warehouses?: StockWarehouse[];
};

/** Per-warehouse breakdown of real-time (RLT) stock. availability: 1 local, 2 local+, 3 nation wide. */
export type StockWarehouse = {
  availability: number;
  code?: string;
  id?: number;
  name: string;
  provider?: string;
  qtyAvailable: number;
};

export type TirePartSummary = {
  hideItem: boolean;
  itemDescription: LanguageMap;
  itemHasDealerPricing: boolean;
  itemHasStock: boolean;
  itemId: string;
  itemImageLink: string;
  itemNationalPartNumber: string;
  itemOriginalEquipmentFlag: string;
  itemPartNumber: string;
  itemPartOption: null;
  itemShippingMetrics: null;
  itemTitle: LanguageMap;
  itemType: PartTypeTire;
  popularIgnoreStock?: boolean;
  supportConsumerOrder: boolean;
  supportDealerOrder: boolean;
};

export type SeasonNumberAsString = "0" | "1" | "2";

export type PrimaryTirePart = {
  formattedTiresize: string;
  itemId: string;
  itemType: PartTypeTire;
  tireCategoryTags: string[];
  tireDescriptionCode: null | string;
  tireDiscontinued: string;
  tireDualFitmentLoadRating: null | string;
  tireImageLink: string;
  tireLoadRating: string;
  tireManufacturerId: string;
  tireManufacturerImage: string;
  tireManufacturerName: string;
  tireManufacturerPartNumber: string;
  tireManufacturerUrl: string;
  tireModelDescription: LanguageMap;
  tireModelId: string;
  tireModelName: string;
  tireNationalPartNumber: string;
  tireOriginalEquipmentFlag: boolean;
  tirePlyRating: string;
  tireRecommendedVehiclesList: TireRecommendedVehicle[];
  tireRunFlat: string;
  tireSeason: SeasonNumberAsString;
  tireSizeSpecId: string;
  tireSpeedIndex: string;
  tireTreadlifeWarranty: {
    CA: string;
    US: string;
  };
  tireVehicleClass: null | string;
  tireWarrantyList: TireWarrantyListItem[];
  treadDepth: string;
};

export type TireWarrantyListItem = {
  warrantyCountryCode: string;
  warrantyLanguageCode: string;
  warrantyText: string;
  warrantyUrl: string;
};

type TireRecommendedVehicle = {
  recommendedVehicleDescription: LanguageMap;
  recommendedVehicleId: string;
  recommendedVehicleManufacturerId: string;
  recommendedVehicleModelId: string;
};

export type PromotionLink = Record<string, PartPricingItem>;

export type TirePartPricing = {
  itemId: string;
  itemType: PartTypeTire;
  link: [] | PromotionLink;
  price: {
    [key: string]: PartPricingItem;
  };
  priceLastUpdated: string;
  prioritizeInSearch: boolean;
};

export type PartPricingItem = {
  contextList: PricingContext[];
  quantity: string;
};

export type PricingContext = {
  displayLabour: string;
  displayPrice: string;
  displaySavings: string;
  linkBody?: LanguageMap;
  linkText?: LanguageMap;
  linkURL?: LanguageMap;
  listPrice: string;
  marketMinimum: boolean;
  pricingContextId: string;
  privateLabour: string;
  privatePrice: string;
  privateSavings: string;
  promotionPricingText: LanguageMap;
  promotionText: LanguageMap;
  showUnitPrice: boolean;
};

export type ResultParameters = {
  dealerId: number;
  originalEquipmentFilter: string;
  season: number;
  vehicleId: number;
  vehicleSubmodelTireSizeId: number;
};

export type TireMetatags = {
  [key: string]: {
    tag_id: string;
    tag_text: string;
    tag_text_fr: string;
  };
};

type PartListSummary = {
  metatagList: TireMetatags;
  minLoadRating: number;
  minPlyRating: string;
  minSpeedRating: string;
  seasonList: {
    partCount: number;
    season: SeasonNumber;
    tireSizeList: {
      partCount: number;
      vehicleSubmodelTiresizeId: string;
    }[];
  }[];
};

/**
 * @see docs/worklog/TW-864-popular-tires-ignore-stock/rfc.md
 * @see docs/worklog/TW-864-popular-tires-ignore-stock/tfc.md
 */
